import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import { authFetch } from '../services/authFetch';
import * as XLSX from 'xlsx';

const AdminLaporanPSMPage = ({ isSidebarOpen, onSidebarToggle }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: false
  });

  // Filter states
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Real data for PSM reports
  const [suratIjinRequests, setSuratIjinRequests] = useState([]);
  const [apresiasiOptions, setApresiasiOptions] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  // Always attempt to fetch data - the API will handle auth checks
  // This prevents issues where the user object might not be loaded yet

  // Date formatting helper function
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day} ${month} ${year}`;
    } catch (error) {
      return '-';
    }
  };

  // Fetch apresiasi options on component mount
  useEffect(() => {
    async function fetchApresiasiOptions() {
      try {
        const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
        // Use admin apresiasis endpoint with high pagination to get all records
        const response = await authFetch(`${apiBase}/api/apresiasis?per_page=1000`, {
          headers: { 'Accept': 'application/json' }
        });
        
        let data = [];
        try {
          const result = await response.json();
          // Handle both direct array and Laravel Resource Collection format
          data = result.data || result || [];
        } catch (err) {
          console.error('Error parsing apresiasi response:', err);
          data = [];
        }
        setApresiasiOptions(data);
      } catch (err) {
        console.error('Failed to fetch apresiasi options:', err);
        setApresiasiOptions([]);
      }
    }
    fetchApresiasiOptions();
  }, []);

  // Fetch reports and admin users
  const fetchReports = async (date = '', status = '') => {
    try {
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      
      // Build query parameters
      const params = new URLSearchParams();
      if (date) params.append('date', date);
      if (status) params.append('status', status);
      
      const queryString = params.toString();
      const url = `${apiBase}/api/admin/psm-reports${queryString ? `?${queryString}` : ''}`;
      
      // Use the imported authFetch instead of window.authFetch
      const res = await authFetch(url, {
        headers: { 'Accept': 'application/json' }
      });
        
        if (!res.ok) {
          const errorText = await res.text();
          console.error('API Error Response:', errorText);
          throw new Error(`Failed to fetch reports: ${res.status} ${errorText}`);
        }
        
        const data = await res.json();
        
        // Handle both Laravel Resource Collection format and direct array
        const sourceData = data.data || data || [];
        
        // Map backend data to UI format for PSM reports
        const mapped = sourceData.map(item => {
          
          // Find apresiasi description from apresiasisId
          let apresiasiDescription = 'N/A';
          let apresiasiPoint = 0;
          
          if (item.apresiasis_id) {
            if (apresiasiOptions.length > 0) {
              const apresiasi = apresiasiOptions.find(opt => opt.id === parseInt(item.apresiasis_id));
              if (apresiasi) {
                apresiasiDescription = apresiasi.description || apresiasi.name || 'N/A';
                apresiasiPoint = apresiasi.point || 0;
              } else {
                apresiasiDescription = `ID: ${item.apresiasis_id}`;
                apresiasiPoint = 0;
              }
            } else {
              // If apresiasi options not loaded yet, show loading or ID
              apresiasiDescription = `Loading...`;
              apresiasiPoint = 0;
            }
          } else if (item.apresiasi?.description) {
            apresiasiDescription = item.apresiasi.description;
            apresiasiPoint = item.apresiasi.point || 0;
          } else if (item.apresiasi?.name) {
            apresiasiDescription = item.apresiasi.name;
            apresiasiPoint = item.apresiasi.point || 0;
          }
          
          return {
            id: item.id,
            nama: item.student?.name || 'N/A',
            student: item.student,
            kelas: item.student?.class || 'N/A',
            tanggalPSM: item.psm_date || '',
            jamMulai: item.start_time || '',
            jamSelesai: item.finish_time || '',
            apresiasi: apresiasiDescription,
            apresiasiPoint: apresiasiPoint,
            apresiasisId: item.apresiasis_id || '',
            deskripsi: item.description || '',
            refleksi: item.reflection || '',
            pic: item.pic || '',
            nomorTelepon: item.pic_contact_number || '',
            adminNote: item.admin_note || '',
            status: item.request_status || 'menunggu',
            statusText: item.request_status === 'disetujui' ? 'Disetujui' : 
                       item.request_status === 'ditolak' ? 'Ditolak' : 'Menunggu',
            images: item.images || [],
            location: item.location || '',
            decision_by: item.decision_by,
            decision_by_id: item.decision_by_id
          };
        });
  
        setSuratIjinRequests(mapped);
      } catch (e) {
        setNotification({
          show: true,
          message: `Error fetching data: ${e.message}`,
          type: 'error'
        });
        
        // Auto-hide error notification after 5 seconds
        setTimeout(() => {
          setNotification({ show: false, message: '', type: 'success' });
        }, 5000);
        
        setSuratIjinRequests([]);
      }
    };
  
  useEffect(() => {
    // Fetch reports always, regardless of apresiasi options status
    fetchReports(dateFilter, statusFilter);
    
    // Fetch admin users for decision maker info
    async function fetchAdminUsers() {
      try {
        const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
        const res = await authFetch(`${apiBase}/api/admin_users`, {
          headers: { 'Accept': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          setAdminUsers(data.data || data);
        }
      } catch (error) {
        console.error('Error fetching admin users:', error);
        setAdminUsers([]);
      }
    }
    fetchAdminUsers();
    
    // eslint-disable-next-line
  }, [apresiasiOptions, dateFilter, statusFilter]);



  const handleStudentClick = (student) => {
    if (student) {
      navigate('/admin/status-siswa', { state: { student } });
    }
  };

  const filteredRequests = suratIjinRequests.filter(request => {
    const q = searchQuery.toLowerCase();
    
    // Only client-side search filter (server handles date and status)
    const matchesSearch = (
      (request.nama && request.nama.toLowerCase().includes(q)) ||
      (request.kelas && request.kelas.toLowerCase().includes(q)) ||
      (request.nomorTelepon && request.nomorTelepon.toLowerCase().includes(q)) ||
      (request.pic && request.pic.toLowerCase().includes(q)) ||
      (request.apresiasi && request.apresiasi.toLowerCase().includes(q)) ||
      (request.refleksi && request.refleksi.toLowerCase().includes(q))
    );
    
    return matchesSearch;
  });

  // Pagination logic
  const totalItems = filteredRequests.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageItems = filteredRequests.slice(startIndex, endIndex);

  // Reset current page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);
  
  // Fetch new data when filters change
  useEffect(() => {
    if (typeof fetchReports === 'function') {
      fetchReports(dateFilter, statusFilter);
    }
    setCurrentPage(1);
  }, [dateFilter, statusFilter]);

  // Excel export function
  const handleExcelExport = async () => {
    try {
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      
      // Build query parameters for export
      const params = new URLSearchParams();
      if (dateFilter) params.append('date', dateFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const queryString = params.toString();
      const url = `${apiBase}/api/admin/psm-reports/export-excel${queryString ? `?${queryString}` : ''}`;
      
      const response = await authFetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Export failed');
      }

      if (!result.data || !Array.isArray(result.data)) {
        throw new Error('Invalid data format received from server');
      }

      // Create Excel workbook
      const worksheet = XLSX.utils.json_to_sheet(result.data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Laporan PSM');

      // Generate filename with current date
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const filename = `laporan_psm_${dateStr}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);

      setNotification({
        show: true,
        message: `Excel berhasil diunduh: ${filename}`,
        type: 'success'
      });

    } catch (error) {
      console.error('Excel export error:', error);
      setNotification({
        show: true,
        message: `Terjadi kesalahan saat export Excel. ${error.message}`,
        type: 'error'
      });
    }
  };

  // Fetch user data from localStorage for sidebar
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Helper function to get admin name
  const getDecisionMakerName = (permit) => {
    if (!permit) {
      return 'Admin';
    }

    // Ideal case: The backend sends the related decision_by object with the name.
    if (permit.decision_by && permit.decision_by.name) {
      return permit.decision_by.name;
    }

    // Fallback: If only decision_by_id is available, look up the name in adminUsers list.
    if (permit.decision_by_id && adminUsers.length > 0) {
      // Use '==' for loose comparison to avoid issues with string vs. number IDs.
      const admin = adminUsers.find(admin => admin.id == permit.decision_by_id);
      return admin ? admin.name : 'Admin';
    }
    
    return 'Admin';
  };

  // Normalize avatar URL
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <PageContainer>
      
      <PageHeader>
        <HeaderContent>
          <div>
            <h1>Laporan PSM</h1>
            <Subtitle>Kelola dan tinjau permintaan PSM</Subtitle>
          </div>
        </HeaderContent>
      </PageHeader>

      <DashboardGrid>
        <SideSection>
          <AdminSidebar user={adminData} accordionState={accordionState} setAccordionState={setAccordionState} onSidebarToggle={onSidebarToggle} />
        </SideSection>
        <MainSection>
          <ContentSection>
            <ToolbarSection>
              <FiltersContainer>
                <SearchContainer>
                  <SearchIcon>🔍</SearchIcon>
                  <SearchInput
                    type="text"
                    placeholder="Cari berdasarkan nama, kelas, PIC, apresiasi..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </SearchContainer>
                
                <FilterGroup>
                  <FilterLabel>Tanggal:</FilterLabel>
                  <FilterInput
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                  />
                </FilterGroup>
                
                <FilterGroup>
                  <FilterLabel>Status:</FilterLabel>
                  <FilterSelect
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">Semua Status</option>
                    <option value="menunggu">Menunggu</option>
                    <option value="disetujui">Disetujui</option>
                    <option value="ditolak">Ditolak</option>
                  </FilterSelect>
                </FilterGroup>

                <ExportButton onClick={handleExcelExport}>
                  📊 Export Excel
                </ExportButton>
                
                {(dateFilter || statusFilter) && (
                  <ClearFiltersButton
                    onClick={() => {
                      setDateFilter('');
                      setStatusFilter('');
                    }}
                  >
                    Hapus Filter
                  </ClearFiltersButton>
                )}
              </FiltersContainer>
            </ToolbarSection>

            <TableContainer>
              <Table>
                <TableHeader>
                  <tr>
                    <th>Tanggal PSM</th>
                    <th>Nama</th>
                    <th>Kelas</th>
                    <th>Jam Mulai</th>
                    <th>Jam Selesai</th>
                    <th>Apresiasi</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </TableHeader>
                <TableBody>
                  {currentPageItems.length > 0 ? (
                    currentPageItems.map((request) => (
                      <TableRow
                        key={request.id}
                        onClick={() => setSelectedPermit(request)}
                        selected={selectedPermit?.id === request.id}
                      >
                        <td>{formatDate(request.tanggalPSM)}</td>
                        <td>
                          <StudentLink onClick={(e) => {
                            e.stopPropagation();
                            handleStudentClick(request.student);
                          }}>
                            {request.nama}
                          </StudentLink>
                        </td>
                        <td>{request.kelas}</td>
                        <td>{request.jamMulai}</td>
                        <td>{request.jamSelesai}</td>
                        <td>{request.apresiasi}</td>

                        <td>
                          <StatusBadge status={request.status}>
                            {request.statusText}
                          </StatusBadge>
                        </td>
                        <td>
                          <ActionButtons>
                            <ActionButton onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPermit(request);
                            }} title="Lihat Detail">
                              👁️
                            </ActionButton>
                          </ActionButtons>
                        </td>
                      </TableRow>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '1rem' }}>
                        Tidak ada laporan
                      </td>
                    </tr>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination Controls */}
            {totalItems > 0 && (
              <PaginationContainer>
                <PaginationInfo>
                  Menampilkan {startIndex + 1}-{Math.min(endIndex, totalItems)} dari {totalItems} data
                </PaginationInfo>
                <PaginationControls>
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹
                  </PaginationButton>
                  
                  <PageNumbers>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <PageNumber
                          key={pageNum}
                          active={pageNum === currentPage}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </PageNumber>
                      );
                    })}
                  </PageNumbers>
                  
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </PaginationButton>
                </PaginationControls>
              </PaginationContainer>
            )}
          </ContentSection>
        </MainSection>
      </DashboardGrid>

      {selectedPermit && (
        <DetailModal onClick={() => setSelectedPermit(null)}>
          <DetailCard onClick={e => e.stopPropagation()}>
            <DetailHeader>
              <DetailHeaderContent>
                <h2>Detail Laporan PSM</h2>
                <StatusContainer>
                  <DetailStatus status={selectedPermit.status}>
                    {selectedPermit.statusText}
                  </DetailStatus>
                  {selectedPermit.status !== 'pending' && selectedPermit.status !== 'menunggu' && (
                    <DecisionMaker>
                      Diputuskan oleh: {getDecisionMakerName(selectedPermit)}
                    </DecisionMaker>
                  )}
                </StatusContainer>
              </DetailHeaderContent>
              <CloseButton onClick={() => setSelectedPermit(null)}>×</CloseButton>
            </DetailHeader>

            <DetailContent>
              <DetailSection>
                <DetailGrid>
                  <DetailGroup>
                    <DetailLabel>Tanggal PSM</DetailLabel>
                    <input
                      type="date"
                      value={selectedPermit.tanggalPSM || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, tanggalPSM: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Nama</DetailLabel>
                    <input
                      type="text"
                      value={selectedPermit.nama || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, nama: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Kelas</DetailLabel>
                    <input
                      type="text"
                      value={selectedPermit.kelas || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, kelas: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Lokasi</DetailLabel>
                    <input
                      type="text"
                      value={selectedPermit.location || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, location: e.target.value })}
                    />
                  </DetailGroup>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <DetailGrid>
                  <DetailGroup>
                    <DetailLabel>Jam Mulai</DetailLabel>
                    <input
                      type="time"
                      value={selectedPermit.jamMulai || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, jamMulai: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Jam Selesai</DetailLabel>
                    <input
                      type="time"
                      value={selectedPermit.jamSelesai || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, jamSelesai: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup full>
                    <DetailLabel>Apresiasi</DetailLabel>
                    <select
                      value={selectedPermit.apresiasisId || ''}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selectedApresiasi = apresiasiOptions.find(opt => opt.id === parseInt(selectedId));
                        setSelectedPermit({
                          ...selectedPermit,
                          apresiasisId: selectedId,
                          apresiasi: selectedApresiasi ? selectedApresiasi.description : '',
                          apresiasiPoint: selectedApresiasi ? selectedApresiasi.point : 0
                        });
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        backgroundColor: 'var(--background)',
                        color: 'var(--text-dark)'
                      }}
                    >
                      <option value="">Pilih Apresiasi</option>
                      {apresiasiOptions.map(option => (
                        <option key={option.id} value={option.id}>
                          {option.description}
                        </option>
                      ))}
                    </select>
                  </DetailGroup>
                  <DetailGroup full>
                    <DetailLabel>Poin Apresiasi</DetailLabel>
                    <input
                      type="number"
                      readOnly
                      value={selectedPermit.apresiasiPoint}
                      onChange={(e) => {
                        setSelectedPermit({
                          ...selectedPermit,
                          apresiasiPoint: parseInt(e.target.value)
                        });
                      }}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        backgroundColor: 'var(--background)',
                        color: 'var(--text-dark)'
                      }}
                    />
                  </DetailGroup>
                  <DetailGroup full>
                    <DetailLabel>Deskripsi</DetailLabel>
                    <textarea
                      value={selectedPermit.deskripsi || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, deskripsi: e.target.value })}
                      style={{ width: '100%', minHeight: 60 }}
                    />
                  </DetailGroup>
                  <DetailGroup full>
                    <DetailLabel>Refleksi</DetailLabel>
                    <textarea
                      value={selectedPermit.refleksi || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, refleksi: e.target.value })}
                      style={{ width: '100%', minHeight: 60 }}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>PIC</DetailLabel>
                    <input
                      type="text"
                      value={selectedPermit.pic || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, pic: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup full>
                    <DetailLabel>Catatan Admin</DetailLabel>
                    <textarea
                      value={selectedPermit.adminNote || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, adminNote: e.target.value })}
                      placeholder="Tambahkan catatan admin (opsional)..."
                      style={{ width: '100%', minHeight: 60 }}
                    />
                  </DetailGroup>
                </DetailGrid>
              </DetailSection>

              {/* Images Section */}
              {selectedPermit.images && selectedPermit.images.length > 0 && (
                <DetailSection>
                  <DetailLabel>Lampiran Gambar</DetailLabel>
                  <ImagesGrid>
                    {selectedPermit.images.map((image, index) => {
                      // Handle different image formats similar to SicknessForm
                      let imgSrc = '';
                      if (typeof image === 'string') {
                        imgSrc = image;
                      } else if (image && image.image_url) {
                        // API returns image objects with image_url property
                        const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                        imgSrc = image.image_url.startsWith('http') 
                          ? image.image_url 
                          : `${baseUrl}/${image.image_url}`;
                      }
                      
                      return (
                        <ImageContainer key={index}>
                          <img
                            src={imgSrc}
                            alt={`Lampiran ${index + 1}`}
                            onClick={() => window.open(imgSrc, '_blank')}
                            onError={(e) => {
                              console.error('Image failed to load:', imgSrc);
                              e.target.style.display = 'none';
                            }}
                          />
                        </ImageContainer>
                      );
                    })}
                  </ImagesGrid>
                </DetailSection>
              )}

              <ActionButtons>
              <RejectButton 
                  type="button" 
                  onClick={async () => {
                    try {
                      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                      
                      const requestData = { 
                        status: 'ditolak',
                        psm_date: selectedPermit.tanggalPSM && selectedPermit.tanggalPSM.trim() !== '' ? selectedPermit.tanggalPSM : null,
                        start_time: selectedPermit.jamMulai ? selectedPermit.jamMulai.substring(0, 5) : null, // Remove seconds
                        finish_time: selectedPermit.jamSelesai ? selectedPermit.jamSelesai.substring(0, 5) : null, // Remove seconds
                        apresiasis_id: selectedPermit.apresiasisId || null,
                        location: selectedPermit.location || null,
                        description: selectedPermit.deskripsi || null,
                        reflection: selectedPermit.refleksi || null,
                        pic: selectedPermit.pic || null,
                        pic_contact_number: selectedPermit.nomorTelepon || null,
                        admin_note: selectedPermit.adminNote || null
                      };
                      
                      const res = await authFetch(`${apiBase}/api/admin/psm-reports/${selectedPermit.id}/status`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                        body: JSON.stringify(requestData)
                      });
                      
                      if (!res.ok) {
                        const errorText = await res.text();
                        console.error('API Error Response:', errorText);
                        throw new Error(`Failed to update status: ${res.status} ${errorText}`);
                      }
                      
                      const result = await res.json();
                      
                      setNotification({
                        show: true,
                        message: 'Laporan PSM ditolak dan data diperbarui!',
                        type: 'info'
                      });
                      
                      setTimeout(() => {
                        setNotification({ show: false, message: '', type: 'success' });
                        fetchReports(dateFilter, statusFilter);
                      }, 3000);
                    } catch (error) {
                      console.error('Error updating status:', error);
                      setNotification({
                        show: true,
                        message: `Error: ${error.message}`,
                        type: 'error'
                      });
                      
                      setTimeout(() => {
                        setNotification({ show: false, message: '', type: 'success' });
                      }, 5000);
                    }
                  }}
                >
                  Tolak
                </RejectButton>
                <ApproveButton 
                  type="button" 
                  onClick={async () => {
                    try {
                      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                      
                      const requestData = { 
                        status: 'disetujui',
                        psm_date: selectedPermit.tanggalPSM && selectedPermit.tanggalPSM.trim() !== '' ? selectedPermit.tanggalPSM : null,
                        start_time: selectedPermit.jamMulai ? selectedPermit.jamMulai.substring(0, 5) : null, // Remove seconds
                        finish_time: selectedPermit.jamSelesai ? selectedPermit.jamSelesai.substring(0, 5) : null, // Remove seconds
                        apresiasis_id: selectedPermit.apresiasisId || null,
                        location: selectedPermit.location || null,
                        description: selectedPermit.deskripsi || null,
                        reflection: selectedPermit.refleksi || null,
                        pic: selectedPermit.pic || null,
                        pic_contact_number: selectedPermit.nomorTelepon || null,
                        admin_note: selectedPermit.adminNote || null
                      };
                      
                      const res = await authFetch(`${apiBase}/api/admin/psm-reports/${selectedPermit.id}/status`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                        body: JSON.stringify(requestData)
                      });
                      
                      if (!res.ok) {
                        const errorText = await res.text();
                        console.error('API Error Response:', errorText);
                        throw new Error(`Failed to update status: ${res.status} ${errorText}`);
                      }
                      
                      const result = await res.json();
                      
                      setNotification({
                        show: true,
                        message: 'Laporan PSM berhasil disetujui dan data diperbarui!',
                        type: 'success'
                      });
                      
                      setTimeout(() => {
                        setNotification({ show: false, message: '', type: 'success' });
                        fetchReports(dateFilter, statusFilter);
                      }, 3000);
                    } catch (error) {
                      console.error('Error updating status:', error);
                      setNotification({
                        show: true,
                        message: `Error: ${error.message}`,
                        type: 'error'
                      });
                      
                      setTimeout(() => {
                        setNotification({ show: false, message: '', type: 'success' });
                      }, 5000);
                    }
                  }}
                >
                  Disetujui
                </ApproveButton>
                
              </ActionButtons>
            </DetailContent>
          </DetailCard>
        </DetailModal>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast type={notification.type}>
          {notification.message}
        </NotificationToast>
      )}
    </PageContainer>
  );
};

// Reuse the same styled components from AdminKonselingPage
const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const ContentSection = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const ToolbarSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: var(--background-light);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  flex: 1;
`;

const SearchIcon = styled.span`
  margin-right: 0.5rem;
  color: var(--text-light);
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 0.875rem;
  color: var(--text-dark);
  outline: none;

  &::placeholder {
    color: var(--text-light);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: var(--background-light);
  
  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: var(--text-dark);
    font-size: 0.875rem;
    border-bottom: 1px solid var(--border);
  }
`;

const TableBody = styled.tbody`
  tr:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  background-color: ${props => props.selected ? 'var(--background-light)' : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }

  td {
    padding: 1rem;
    font-size: 0.875rem;
    color: var(--text-dark);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#fff3e0';
      case 'approved':
      case 'disetujui': return '#e6f8f5';
      case 'rejected':
      case 'ditolak': return '#ffe6e6';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#e67700';
      case 'approved':
      case 'disetujui': return '#2ec27e';
      case 'rejected':
      case 'ditolak': return '#c01c28';
      default: return 'var(--text-dark)';
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }
`;

const DetailModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 1000;
`;

const DetailCard = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border);
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const DetailHeaderContent = styled.div`
  flex: 1;
  margin-right: 1rem;
`;

const DetailStatus = styled(StatusBadge)`
  font-size: 0.875rem;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DecisionMaker = styled.div`
  font-size: 0.75rem;
  color: var(--text-light);
  font-style: italic;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  padding: 0.25rem;
  line-height: 1;
  
  &:hover {
    color: var(--text-dark);
  }
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DetailGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  grid-column: ${props => props.full ? '1 / -1' : 'auto'};
`;

const DetailLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-light);
`;

const DetailValue = styled.div`
  font-size: 0.875rem;
  color: var(--text-dark);
`;

const ApproveButton = styled.button`
  flex: 1;
  background-color: var(--success, #2ec27e);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.938rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1.5rem;
  
  &:hover {
    background-color: #26a96b;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RejectButton = styled(ApproveButton)`
  background-color: var(--danger, #c01c28);
  
  &:hover {
    background-color: #a01622;
  }
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1rem;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  min-height: 120px;
  max-height: 120px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
`;

const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 1rem;
  border-radius: 8px;
  background-color: ${props => props.type === 'success' ? 'var(--success, #2ec27e)' : 'var(--danger, #c01c28)'};
  color: white;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideIn 0.3s ease-out forwards;
  max-width: 350px;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const StudentLink = styled.span`
  color: var(--primary, #3b82f6);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: var(--primary-dark, #2563eb);
    text-decoration: underline;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border);
  background-color: var(--background-light);
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: var(--text-light);
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  background: ${props => props.disabled ? 'var(--background-light)' : 'var(--background)'};
  border: 1px solid var(--border);
  color: ${props => props.disabled ? 'var(--text-light)' : 'var(--text-dark)'};
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
`;

const PageNumber = styled.button`
  background-color: ${props => props.active ? 'var(--primary)' : 'var(--background)'};
  color: ${props => props.active ? 'white' : 'var(--text-dark)'};
  border: 1px solid ${props => props.active ? 'var(--primary)' : 'var(--border)'};
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  min-width: 2.5rem;
  transition: all 0.2s;

  &:hover {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }
`;

// Filter components
const FiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.25rem;
  }
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-dark);
  white-space: nowrap;
`;

const FilterInput = styled.input`
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  color: var(--text-dark);
  background-color: var(--background);
  min-width: 140px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const FilterSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  color: var(--text-dark);
  background-color: var(--background);
  min-width: 140px;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const ExportButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: var(--primary, #3b82f6);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background-color: var(--primary-dark, #2563eb);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const ClearFiltersButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: var(--background-light);
  color: var(--text-dark);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background-color: var(--danger, #c01c28);
    color: white;
    border-color: var(--danger, #c01c28);
  }
`;

export default AdminLaporanPSMPage;